/* PeriodPicker — sélecteur de période flexible (année / trimestre / mois).
 *
 * Trois modes de navigation cohabitent dans un même popover :
 *   - Année       : 2025, 2026, 2027, …
 *   - Trimestre   : T1 2026, T2 2026, …
 *   - Mois        : Mars 2026, …
 *
 * État persisté en localStorage sous "noam_period" : { mode, year, quarter?, month? }
 * Diffusion via l'event "noam:period-changed" (detail = { mode, year, ..., label }).
 * Sync backend best-effort via POST /executive/period (label texte uniquement).
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";

const STORAGE_KEY = "noam_period";
const MODES = ["year", "quarter", "month"];
const CURRENT_YEAR = new Date().getFullYear();

// --- Default state ---
function getDefaultState() {
  return { mode: "quarter", year: CURRENT_YEAR, quarter: Math.ceil((new Date().getMonth() + 1) / 3), month: new Date().getMonth() + 1 };
}

// --- localStorage helpers ---
export function loadPeriod() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultState();
    const parsed = JSON.parse(raw);
    if (!parsed || !MODES.includes(parsed.mode)) return getDefaultState();
    return { ...getDefaultState(), ...parsed };
  } catch {
    return getDefaultState();
  }
}

function savePeriod(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

// --- Label formatting (uses i18n) ---
export function formatPeriodLabel(state, t) {
  if (!state) return "";
  if (state.mode === "year") return `${state.year}`;
  if (state.mode === "quarter") return `T${state.quarter} ${state.year}`;
  const monthKey = `periods.month_${state.month}`;
  const fallback = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"][state.month] || "";
  return `${t(monthKey, { defaultValue: fallback })} ${state.year}`;
}

// --- Navigation helpers (prev/next) ---
function shift(state, delta) {
  const out = { ...state };
  if (state.mode === "year") {
    out.year = state.year + delta;
  } else if (state.mode === "quarter") {
    const total = state.year * 4 + (state.quarter - 1) + delta;
    out.year = Math.floor(total / 4);
    out.quarter = (total % 4 + 4) % 4 + 1;
  } else {
    const total = state.year * 12 + (state.month - 1) + delta;
    out.year = Math.floor(total / 12);
    out.month = (total % 12 + 12) % 12 + 1;
  }
  return out;
}

export default function PeriodPicker() {
  const { t } = useTranslation();
  const [state, setState] = useState(loadPeriod);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState({ top: 56, left: 16 });
  const label = useMemo(() => formatPeriodLabel(state, t), [state, t]);

  // Persist + broadcast on change
  useEffect(() => {
    savePeriod(state);
    const detail = { ...state, label };
    window.dispatchEvent(new CustomEvent("noam:period-changed", { detail }));
    // Best-effort backend sync (fire-and-forget)
    api.post("/executive/period", { period: label }).catch(() => {});
  }, [state, label]);

  // Listen to external updates (other tabs, etc.)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try { setState(JSON.parse(e.newValue)); } catch { /* ignore */ }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Close popover on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => {
      if (!e.target.closest?.("[data-period-picker]")) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const setMode = (mode) => setState((s) => ({ ...s, mode }));
  const navigate = (delta) => setState((s) => shift(s, delta));

  return (
    <div className="relative" data-period-picker data-testid="period-picker">
      <button
        type="button"
        onClick={(e) => {
          if (!open) {
            const r = e.currentTarget.getBoundingClientRect();
            setAnchor({ top: Math.round(r.bottom + 6), left: Math.round(r.left) });
          }
          setOpen((o) => !o);
        }}
        className="flex items-center gap-1 text-white hover:text-[#378ADD] transition-colors"
        data-testid="period-dropdown-btn"
        title={t("periods.title", { defaultValue: "Période" })}
      >
        <CalendarIcon className="w-3.5 h-3.5 opacity-70" />
        <span>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="noam-card p-3 min-w-[260px] space-y-3"
          style={{ position: "fixed", top: anchor.top, left: anchor.left, zIndex: 9999 }}
          data-period-picker
          data-testid="period-dropdown-menu"
        >
          {/* Mode tabs */}
          <div className="flex items-center gap-1 bg-white/[0.04] rounded p-0.5 border border-white/5">
            {MODES.map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex-1 text-[11px] px-2 py-1 rounded transition-colors ${
                  state.mode === m
                    ? "bg-[#378ADD]/20 text-[#378ADD] border border-[#378ADD]/40"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
                data-testid={`period-mode-${m}`}
              >
                {t(`periods.mode_${m}`, { defaultValue: { year: "Année", quarter: "Trimestre", month: "Mois" }[m] })}
              </button>
            ))}
          </div>

          {/* Prev / Current / Next */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-1.5 rounded border border-white/10 hover:bg-white/5 text-zinc-300"
              data-testid="period-prev"
              aria-label={t("periods.prev", { defaultValue: "Précédent" })}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <div className="flex-1 text-center text-sm font-semibold text-white" data-testid="period-current-label">
              {label}
            </div>
            <button
              type="button"
              onClick={() => navigate(1)}
              className="p-1.5 rounded border border-white/10 hover:bg-white/5 text-zinc-300"
              data-testid="period-next"
              aria-label={t("periods.next", { defaultValue: "Suivant" })}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Grid by mode */}
          {state.mode === "year" && (
            <div className="grid grid-cols-3 gap-1.5">
              {[-2, -1, 0, 1, 2, 3].map((delta) => {
                const y = state.year + delta;
                const active = delta === 0;
                return (
                  <button
                    key={y}
                    onClick={() => setState((s) => ({ ...s, year: y }))}
                    className={`text-[11px] py-1.5 rounded border transition-colors ${
                      active
                        ? "border-[#378ADD] bg-[#378ADD]/15 text-[#378ADD]"
                        : "border-white/10 text-zinc-300 hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`period-year-${y}`}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          )}

          {state.mode === "quarter" && (
            <div className="grid grid-cols-4 gap-1.5">
              {[1, 2, 3, 4].map((q) => {
                const active = q === state.quarter;
                return (
                  <button
                    key={q}
                    onClick={() => setState((s) => ({ ...s, quarter: q }))}
                    className={`text-[11px] py-1.5 rounded border transition-colors ${
                      active
                        ? "border-[#378ADD] bg-[#378ADD]/15 text-[#378ADD]"
                        : "border-white/10 text-zinc-300 hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`period-quarter-${q}`}
                  >
                    T{q}
                  </button>
                );
              })}
            </div>
          )}

          {state.mode === "month" && (
            <div className="grid grid-cols-3 gap-1.5">
              {Array.from({ length: 12 }).map((_, idx) => {
                const m = idx + 1;
                const active = m === state.month;
                const monthKey = `periods.month_short_${m}`;
                const fallback = ["", "Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin",
                  "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."][m];
                return (
                  <button
                    key={m}
                    onClick={() => setState((s) => ({ ...s, month: m }))}
                    className={`text-[11px] py-1.5 rounded border transition-colors ${
                      active
                        ? "border-[#378ADD] bg-[#378ADD]/15 text-[#378ADD]"
                        : "border-white/10 text-zinc-300 hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`period-month-${m}`}
                  >
                    {t(monthKey, { defaultValue: fallback })}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
